import {
  AttendanceMonth,
  getAvailableAttendanceMonths,
  getAvailableGiustificativi,
  getCurrentAttendance,
  getOriginalAttendance,
  saveAttendance,
  submitAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useIsTablet } from "@/config/hooks";
import { attendanceToTempAttendance } from "./attendanceUtils";

export function useAttendances() {
  const { selectedMandateId, lang } = useContext(PspContext);
  const {
    attendanceMonth,
    setAttendanceMonth,
    selectedMonth,
    selectedYear,
    setTempAttendanceMonth,
    tempAttendanceMonth,
    setAvailableMonths,
    setSelectedMonth,
    setSelectedYear,
    setIsSubmitted,
    setSubmittedAt,
    setAvailableGiustificativi,
  } = useContext(AttendancesContext);
  const isTablet = useIsTablet();
  const [canRestoreOriginal, setCanRestoreOriginal] = useState(false);
  const [restoreOriginalConfirmationOpen, setRestoreOriginalConfirmationOpen] =
    useState(false);
  const [
    submitAttendanceConfirmationOpen,
    setSubmitAttendanceConfirmationOpen,
  ] = useState(false);

  useEffect(() => {
    if (selectedMonth === undefined || selectedYear === undefined) return;
    if (!selectedMandateId) return;

    getCurrentAttendance({
      query: {
        mandate_id: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAttendanceMonth(data.data);
      setTempAttendanceMonth(attendanceToTempAttendance(data.data));
      setIsSubmitted(data.isSubmitted);
      setCanRestoreOriginal(data.canRestoreOriginal);
      setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
    });
  }, [
    lang,
    selectedMandateId,
    selectedMonth,
    selectedYear,
    setAttendanceMonth,
    setIsSubmitted,
    setSubmittedAt,
    setTempAttendanceMonth,
  ]);

  useEffect(() => {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    getAvailableGiustificativi({
      path: { mandate_id: selectedMandateId },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAvailableGiustificativi(data);
    });
  }, [lang, selectedMandateId, setAvailableGiustificativi]);

  // get last available month if no month is selected
  useEffect(() => {
    if (selectedMonth !== undefined && selectedYear !== undefined) return;
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    let lastAvailableMonth: number;
    let lastAvailableYear: number;
    getAvailableAttendanceMonths({
      query: { mandate_id: selectedMandateId },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAvailableMonths(data);
      if (data.length === 0) {
        toast(translate("noAttendanceAvailable", lang));
        return;
      }
      lastAvailableMonth = data[0].month;
      lastAvailableYear = data[0].year;
      setSelectedMonth(lastAvailableMonth);
      setSelectedYear(lastAvailableYear);

      if (!attendanceMonth && !tempAttendanceMonth) {
        getCurrentAttendance({
          query: {
            mandate_id: selectedMandateId,
            month: lastAvailableMonth,
            year: lastAvailableYear,
          },
        }).then(({ data, error }) => {
          if (error) {
            toast(translate("somethingWentWrong", lang));
            return;
          }
          setAttendanceMonth(data.data);
          setTempAttendanceMonth(attendanceToTempAttendance(data.data));
          setIsSubmitted(data.isSubmitted);
          setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
        });
      }
    });
  }, [
    attendanceMonth,
    lang,
    selectedMandateId,
    selectedMonth,
    selectedYear,
    setAttendanceMonth,
    setAvailableMonths,
    setIsSubmitted,
    setSelectedMonth,
    setSelectedYear,
    setSubmittedAt,
    setTempAttendanceMonth,
    tempAttendanceMonth,
  ]);

  function onSubmitAttendance() {
    if (!attendanceMonth || !tempAttendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        // eslint-disable-next-line max-len
        "User tried submitting attendance but either attendanceMonth or tempAttendanceMonth or both are not defined.",
      );
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (attendanceMonth.id !== tempAttendanceMonth.id) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "The IDs of attendanceMonth and tempAttendanceMonth differ.",
      );
    }
    submitAttendance({
      path: { attendance_id: attendanceMonth.id },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setAttendanceMonth(data.data);
        setTempAttendanceMonth(attendanceToTempAttendance(data.data));
        setIsSubmitted(data.isSubmitted);
        setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
        setSubmitAttendanceConfirmationOpen(false);
      }
    });
  }

  function onRestoreOriginal() {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    getOriginalAttendance({
      query: {
        mandate_id: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error, status }) => {
      if (error && status === 409) {
        toast(translate("restoreOriginalWentWrong", lang));
      }
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setTempAttendanceMonth(attendanceToTempAttendance(data.data));
        setAttendanceMonth(data.data);
        setIsSubmitted(data.isSubmitted);
        setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
        toast(
          translate(
            isTablet ? "originalRestoredDesktop" : "originalRestoredMobile",
            lang,
          ),
        );
        if (!isTablet) {
          saveAttendance({
            body: {
              data: data.data.attendances,
              mandateId: selectedMandateId,
              month: selectedMonth,
              year: selectedYear,
            },
            query: {
              draft: true,
            },
          });
        }
        setRestoreOriginalConfirmationOpen(false);
      }
    });
  }

  function onRestoreEmployee(employeeMatricola: number) {
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    getOriginalAttendance({
      query: {
        mandate_id: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }

      const originalEmployeeAttendance = data.data.attendances.find(
        (a) => a.employee.matricola === employeeMatricola,
      );
      if (!originalEmployeeAttendance) {
        toast(translate("somethingWentWrong", lang));
        throw new Error(
          "Employee matricola does not exist in this attendance.",
        );
      }

      const newAttendance: AttendanceMonth = {
        ...attendanceMonth,
        attendances: attendanceMonth.attendances.map((a) =>
          a.employee.matricola === employeeMatricola
            ? originalEmployeeAttendance
            : a,
        ),
      };

      setAttendanceMonth(newAttendance);
      setTempAttendanceMonth(attendanceToTempAttendance(newAttendance));
      saveAttendance({
        query: { draft: true },
        body: {
          data: newAttendance.attendances,
          mandateId: selectedMandateId,
          month: selectedMonth,
          year: selectedYear,
        },
      });
    });
  }

  return {
    onSubmitAttendance,
    onRestoreOriginal,
    restoreOriginalConfirmationOpen,
    setRestoreOriginalConfirmationOpen,
    isTablet,
    canRestoreOriginal,
    submitAttendanceConfirmationOpen,
    setSubmitAttendanceConfirmationOpen,
    lang,
    onRestoreEmployee,
  };
}
